import { useData } from "../../../../runtime/provider";
import LBox from "@components/LBox";
import LText from "@components/LText";
import QRCode from "react-native-qrcode-svg";
import { router, useLocalSearchParams } from "expo-router";
import { useQuery } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { trpc, RouterOutputs } from "@utils/trpc";
import { viewTicketWireSchema, type ViewTicket } from "@libs/wire";
import {
  Platform,
  useWindowDimensions,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { useCallback } from "react";
import { getViewPort } from "@utils/web/config";
import { theme } from "@theme";
import logo from "@assets/images/icon-foreground.png";
import LArtwork from "@components/LArtwork";
import ViewAppButton from "@components/TopBar/ViewAppButton";
import BackgroundCircles from "@components/Accesses/AccessDetails/components/common/Background";
import Handle from "@components/Accesses/AccessDetails/components/common/Handle";
import {
  useSafeAreaInsets,
  initialWindowMetrics,
} from "react-native-safe-area-context";
import { useAccessViewport } from "@components/Accesses/AccessDetails/config";
import Close from "@components/Accesses/AccessDetails/components/common/Close";
import { AccessDetailsDismiss } from "@components/Accesses/AccessDetails/types";
import { TicketCarousel } from "@components/Accesses/AccessDetails/components/event/TicketCarousel";
import { VendorTickets } from "@components/Accesses/AccessDetails/components/event/VendorTickets";
import { VendorTicketsHelpSheet } from "@components/Accesses/AccessDetails/components/event/VendorTicketsHelpSheet";
import LIcon from "@components/LIcon";
import {
  PURCHASED_TICKETS_COPY,
  VENDOR_TICKETS_EVENTS,
  createTicketPageDetails,
  engineTicketQrValue,
} from "@libs/shared-ui";
import LPressable from "@components/LPressable";
import {
  BottomSheetHost,
  BottomSheetManagerProvider,
  useBottomSheetManager,
} from "react-native-bottom-sheet-stack";

type ViewTicketAccess = RouterOutputs["accesses"]["viewTicket"]["access"];

const ViewTicketContent = ({
  access,
  collect,
  dismiss,
}: {
  access: ViewTicketAccess;
  /** The read's `collect`: My Tickets for a purchase bought on a seller's
   *  site, compiled by the server for this viewer; null for engine
   *  tickets. */
  collect: ViewTicket["collect"];
  dismiss: AccessDetailsDismiss;
}) => {
  const screen = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const bottomSheetManager = useBottomSheetManager();

  const dimensions = getViewPort(screen);
  const width = dimensions.width - theme.spacing.m1 * 2;
  const qrBoxWidth = width * 0.65;

  // Type guard: ensure access is an event with userTickets
  if (access.type !== "event" || !access.userTickets) {
    return (
      <LBox alignItems="center" justifyContent="center" flex={1}>
        <LText variant="h1">{PURCHASED_TICKETS_COPY.noTickets}</LText>
      </LBox>
    );
  }

  const tickets = access.userTickets;
  // Bought on a seller's site: no QR code of ours; what `collect` says, the
  // seller's codes, and its directions and account behind the "?".
  const help = collect ? (
    <LPressable
      accessibilityRole="button"
      accessibilityLabel={PURCHASED_TICKETS_COPY.help}
      onPress={() =>
        bottomSheetManager.open(
          <VendorTicketsHelpSheet accessId={access.id} collect={collect} />,
          { mode: "push" },
        )
      }
      sentryEventName={VENDOR_TICKETS_EVENTS.help}
      analytics={{ listing_id: collect.listingId }}
    >
      <LIcon name="help" size={28} colors={{ color: "white" }} />
    </LPressable>
  ) : null;

  // Venue-local `{ date, time }` resolved server-side (2026-07-23 convention):
  // render literally. `time: null` (date-only source) hides the fake "Door
  // Opens" / "Show Starts" clock rows — no made-up time. Worded as Link in
  // Bio's tickets page words them (`createTicketPageDetails`).
  const details = createTicketPageDetails(access);

  if (tickets.length === 0) {
    return (
      <LBox alignItems="center" justifyContent="center" flex={1}>
        <LText variant="h1">{PURCHASED_TICKETS_COPY.noTickets}</LText>
      </LBox>
    );
  }

  // Get image URL for artwork
  const imageUrl =
    "coverImageUrl" in access && access.coverImageUrl
      ? access.coverImageUrl
      : access.artistImage;

  return (
    // The modal's own height, not the screen's (the sheet sits below the
    // status bar): what is taller than it scrolls.
    <LBox flex={1}>
      <BackgroundCircles imageUrl={null} />
      <ScrollView
        contentContainerStyle={{
          backgroundColor: "transparent",
          padding: 20,
          paddingTop: theme.spacing.s4,
          paddingBottom:
            Platform.OS === "android"
              ? initialWindowMetrics?.insets?.bottom ?? 20
              : insets.bottom + theme.spacing.s3,
          gap: theme.spacing.s3,
          // At least the modal's height, so a short page still spreads out,
          // and taller when its content is: then it scrolls.
          flexGrow: 1,
          // Live width, like `width` above: paging needs the carousel to be
          // exactly one ticket wide.
          width: dimensions.width,
        }}
        showsVerticalScrollIndicator={false}
      >
        {Platform.OS !== "web" && <Handle />}

        <LBox flex={1} justifyContent="space-between">
          <LBox gap="m1">
            <LBox>
              <LBox
                position="relative"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                {/* The right side's twin, unseen and untouchable: it keeps
                    the title centred. */}
                <LBox
                  flexDirection="row"
                  gap="m1"
                  alignItems="center"
                  opacity={0}
                  pointerEvents="none"
                >
                  <ViewAppButton
                    text="Get the App"
                    url={`/access/${access.id}`}
                    style={{ paddingVertical: theme.spacing.s2 }}
                  />
                  {help}
                  <Close
                    onPress={() => dismiss()}
                    sentryEventName="ViewTickets.Close"
                  />
                </LBox>
                <LBox>
                  <LText variant="Heading/H2/SemiBold" textAlign="center">
                    {PURCHASED_TICKETS_COPY.title}
                  </LText>
                </LBox>
                <LBox flexDirection="row" gap="m1" alignItems="center">
                  <ViewAppButton
                    text="Get the App"
                    url={`/access/${access.id}`}
                    style={{ paddingVertical: theme.spacing.s2 }}
                  />
                  {help}
                  <Close
                    onPress={() => dismiss()}
                    sentryEventName="ViewTickets.Close"
                  />
                </LBox>
              </LBox>
            </LBox>

            <LBox flexDirection="row" gap="m1" alignItems="center">
              <LArtwork url={imageUrl} size={85} borderRadius="m" />
              <LBox gap={"s2"} flex={1} minWidth={0}>
                <LText variant="Body/B1/Medium" color="whiteTranslucent60">
                  {PURCHASED_TICKETS_COPY.eventLabel}
                </LText>
                <LText variant="Heading/H1/Bold">{access.title}</LText>
                <LText variant="Body/B1/Medium">{details.dateLine}</LText>
              </LBox>
            </LBox>
          </LBox>

          {collect ? (
            <VendorTickets
              collect={collect}
              width={width}
              boxWidth={qrBoxWidth}
            />
          ) : (
            <TicketCarousel
              width={width}
              boxWidth={qrBoxWidth}
              pages={tickets.map((ticket) => ({
                key: ticket.id,
                box:
                  ticket.isWaitlist ?? false ? (
                    <LBox
                      bg="white"
                      borderRadius="m"
                      width={qrBoxWidth}
                      height={qrBoxWidth}
                      overflow="hidden"
                      alignItems="center"
                      justifyContent="center"
                      borderWidth={2}
                      borderColor="artistGreen"
                    >
                      <LText
                        variant="h2"
                        color="black"
                        textAlign="center"
                        px="s3"
                      >
                        {PURCHASED_TICKETS_COPY.waitlistTitle}
                      </LText>
                      <LText
                        variant="bodyM"
                        color="whiteTranslucent60"
                        textAlign="center"
                        px="s3"
                        mt="s2"
                      >
                        {PURCHASED_TICKETS_COPY.waitlistBody}
                      </LText>
                    </LBox>
                  ) : (
                    <LBox
                      bg="white"
                      borderRadius="m"
                      width={qrBoxWidth}
                      height={qrBoxWidth}
                      overflow="hidden"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <QRCode
                        size={qrBoxWidth - 20}
                        value={engineTicketQrValue(ticket.redeemCode)}
                        logo={logo}
                        logoSize={65}
                        logoBackgroundColor="white"
                        logoMargin={-10}
                      />
                    </LBox>
                  ),
              }))}
            />
          )}

          <LBox
            px="m1"
            gap="m1"
            flexDirection="row"
            justifyContent="space-between"
          >
            <LBox gap="m1" flex={1} flexShrink={1} minWidth={0}>
              <LBox gap={"s1"}>
                <LText variant="Body/B2/Medium" color="whiteTranslucent60">
                  {PURCHASED_TICKETS_COPY.venue}
                </LText>
                <LText variant="Body/B1/Medium">{details.venue}</LText>
              </LBox>
              <LBox gap={"s1"}>
                <LText variant="Body/B2/Medium" color="whiteTranslucent60">
                  {PURCHASED_TICKETS_COPY.address}
                </LText>
                <LText variant="Body/B1/Medium">{details.address}</LText>
              </LBox>
            </LBox>
            {details.doorOpens && (
              <LBox gap="m1" flexShrink={0}>
                <LBox gap={"s1"}>
                  <LText
                    variant="Body/B2/Medium"
                    color="whiteTranslucent60"
                    textAlign="right"
                  >
                    {PURCHASED_TICKETS_COPY.doorOpens}
                  </LText>
                  <LText variant="Body/B1/Medium" textAlign="right">
                    {details.doorOpens}
                  </LText>
                </LBox>
                <LBox gap={"s1"}>
                  <LText
                    variant="Body/B2/Medium"
                    color="whiteTranslucent60"
                    textAlign="right"
                  >
                    {PURCHASED_TICKETS_COPY.showStarts}
                  </LText>
                  <LText variant="Body/B1/Medium" textAlign="right">
                    {details.showStarts}
                  </LText>
                </LBox>
              </LBox>
            )}
          </LBox>
        </LBox>
      </ScrollView>
    </LBox>
  );
};

const ViewTicketLoading = () => {
  const { width: screenWidth, height: screenHeight } = useAccessViewport();
  const insets = useSafeAreaInsets();

  return (
    <LBox minHeight={screenHeight}>
      <BackgroundCircles imageUrl={null} />
      <ScrollView
        contentContainerStyle={{
          backgroundColor: "transparent",
          padding: 20,
          paddingTop: theme.spacing.s4,
          paddingBottom:
            Platform.OS === "android"
              ? initialWindowMetrics?.insets?.bottom ?? 20
              : insets.bottom + theme.spacing.s3,
          gap: theme.spacing.s3,
          flex: 1,
          width: screenWidth,
        }}
        showsVerticalScrollIndicator={false}
      >
        {Platform.OS !== "web" && <Handle />}
        <LBox alignItems="center" justifyContent="center" flex={1}>
          <LText variant="h1">{PURCHASED_TICKETS_COPY.loading}</LText>
        </LBox>
      </ScrollView>
    </LBox>
  );
};

const ViewTicketScreen = () => {
  const session = useData();
  const { width: screenWidth, height: screenHeight } = useAccessViewport();
  const { accessId, token } = useLocalSearchParams<{
    accessId: string;
    token?: string;
  }>();

  const dismiss: AccessDetailsDismiss = useCallback((targetScreen) => {
    if (targetScreen) {
      router.replace(targetScreen);
      return;
    }
    router.back();
  }, []);

  // Always enable query - backend handles auth (token or authenticated user).
  // Durable cache: cached ticket data shows immediately on cold-start with
  // no network (the venue scenario), then refetch reconciles when signal
  // returns. Without durable cache, an MMKV-rehydrated react-query cache
  // *used* to do this; Phase 2.6 retired MMKV.
  const { data, isLoading } = useQuery(
    session.cache.durableQueryOptions<ViewTicket>({
      queryKey: getQueryKey(trpc.accesses.viewTicket, {
        accessId,
        token: token,
      }),
      queryFn: () =>
        session.api.accesses.viewTicket.query({
          accessId,
          token: token,
        }),
      schema: viewTicketWireSchema,
      refetchOnMount: "always", // Ticket status can change server-side (e.g., waitlist promotion)
      refetchOnWindowFocus: true,
      staleTime: 5 * 60 * 1000,
    }),
  );

  const insets = useSafeAreaInsets();

  // A native modal, which covers the app-wide sheet host: the sheet opened
  // from here (the vendor tickets' "?") needs a host of its own inside the
  // modal, as the access Details has.
  return (
    <BottomSheetManagerProvider id={`view-tickets-${accessId}`}>
      <View style={styles.fill}>
        {(() => {
          if (isLoading) {
            return <ViewTicketLoading />;
          }

          if (!data?.access) {
            return (
              <LBox minHeight={screenHeight}>
                <BackgroundCircles imageUrl={null} />
                <ScrollView
                  contentContainerStyle={{
                    backgroundColor: "transparent",
                    padding: 20,
                    paddingTop: theme.spacing.s4,
                    paddingBottom:
                      Platform.OS === "android"
                        ? initialWindowMetrics?.insets?.bottom ?? 20
                        : insets.bottom + theme.spacing.s3,
                    gap: theme.spacing.s3,
                    flex: 1,
                    width: screenWidth,
                  }}
                  showsVerticalScrollIndicator={false}
                >
                  {Platform.OS !== "web" && <Handle />}
                  <LBox alignItems="center" justifyContent="center" flex={1}>
                    <LText variant="h1">
                      {PURCHASED_TICKETS_COPY.noTickets}
                    </LText>
                  </LBox>
                </ScrollView>
              </LBox>
            );
          }

          return (
            <ViewTicketContent
              access={data.access}
              collect={data.collect}
              dismiss={dismiss}
            />
          );
        })()}
        <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
          <BottomSheetHost />
        </View>
      </View>
    </BottomSheetManagerProvider>
  );
};

const styles = StyleSheet.create({ fill: { flex: 1 } });

export default ViewTicketScreen;
